import type { SignalSource, Signal, SignalFilters } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type SignalCreateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalCreateDTO";
import type SignalUpdateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalUpdateDTO";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";
import {
    ecommerceChangeToApiPayload,
    isCompleteEcommerceBucketTransition,
    isCompleteEcommerceChange,
} from "app/pages/sales-intelligence/sub-modules/signals/utils/ecommerce/ecommerceFilterNormalization";

import SignalType from "../core/SignalType";
import type { SignalTemplateDeps } from "../templates/SignalTemplate";
import SignalTemplate from "../templates/SignalTemplate";

import { ECOMMERCE_CHANGE_SUBTYPES } from "./constants";
import type EcommerceSignalFilters from "./EcommerceSignalFilters";

type EcommerceSignalTemplateDeps = Omit<SignalTemplateDeps<EcommerceSignalFilters>, "signalType">;

export default class EcommerceSignalTemplate extends SignalTemplate<EcommerceSignalFilters> {
    constructor(deps: EcommerceSignalTemplateDeps) {
        super({ ...deps, signalType: SignalType.Ecommerce });
    }

    matchesSignal(signal: Readonly<Signal<EcommerceSignalFilters>>): boolean {
        return signal.signalSubType === this.signalSubType;
    }

    isValid(): boolean {
        return typeof this.signalSubType !== "undefined";
    }

    getSignalCreateDTO<F extends SignalFilters>(
        filters: Readonly<F>,
        signalSource: SignalSource,
    ): Readonly<SignalCreateDTO<F>> {
        const ecommerceFilters = filters as EcommerceSignalFilters;

        if (ECOMMERCE_CHANGE_SUBTYPES.has(this.signalSubType as string)) {
            const change = ecommerceFilters.change;

            if (!isCompleteEcommerceChange(change)) {
                throw new Error("EcommerceSignalTemplate: incomplete change filters");
            }

            return {
                isDefault: false,
                signalSource,
                signalType: this.signalType,
                signalSubType: this.signalSubType,
                lifecycleStatus: this.lifecycleStatus,
                filters: {
                    change: ecommerceChangeToApiPayload(change),
                } as unknown as F,
            };
        }

        const bucketTransition = ecommerceFilters.bucketTransition;

        if (!isCompleteEcommerceBucketTransition(bucketTransition)) {
            throw new Error("EcommerceSignalTemplate: incomplete bucket transition filters");
        }

        return {
            isDefault: false,
            signalSource,
            signalType: this.signalType,
            signalSubType: this.signalSubType,
            lifecycleStatus: this.lifecycleStatus,
            filters: {
                bucketTransition: {
                    metric: bucketTransition.metric,
                    bucket: bucketTransition.bucket,
                },
            } as unknown as F,
        };
    }

    getSignalUpdateDTO<F extends SignalFilters>(
        uiSignal: Readonly<UISignal<F>>,
        filters: Readonly<F>,
    ): Readonly<SignalUpdateDTO<F>> {
        const { id, isDefault, signalType } = uiSignal;
        const created = this.getSignalCreateDTO(filters, uiSignal.signalSource);

        return {
            id,
            isDefault,
            signalType,
            signalSubType: created.signalSubType,
            filters: created.filters as F,
        };
    }
}
